<template>
  <b-modal :id="modalId" :title="modalTitle" hide-footer @show="resetForm()" @hidden="resetForm()">
    <b-alert variant="danger" :show="errors.length > 0">{{ errors[0] }}</b-alert>

    <p><strong>Username: </strong>{{ user.username }}</p>
    <p><strong>Email: </strong>{{ user.email }}</p>

    <ValidationObserver v-slot="{ invalid }">
      <b-form @submit.stop.prevent="editUser()">
        <template v-if="fieldToEdit === 'password'">
          <b-form-group v-if="isCurrentUser" label="Current Password" label-for="input-current-password" label-class="font-weight-bold">
            <b-form-input v-model="form.currentPassword" type="password" id="input-current-password"></b-form-input>
          </b-form-group>

          <ValidationProvider name="Password" rules="required|min:8" v-slot="validationContext">
            <b-form-group label="New Password" label-for="input-new-password" label-class="font-weight-bold">
              <b-form-input v-model="form.newPassword" type="password" id="input-new-password" :state="getValidationState(validationContext)"></b-form-input>
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                Password must be <strong>at least eight (8)</strong> characters long.
              </b-form-invalid-feedback>
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider v-if="isCurrentUser" name="ConfirmPassword" rules="required|password:@Password" v-slot="validationContext">
            <b-form-group label="Confirm Password" label-for="input-confirm-password" label-class="font-weight-bold">
              <b-form-input v-model="form.confirmPassword" type="password" id="input-confirm-password" :state="getValidationState(validationContext)"></b-form-input>
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                Password confirmation does not match.
              </b-form-invalid-feedback>
            </b-form-group>
          </ValidationProvider>
        </template>

        <ValidationProvider v-if="fieldToEdit === 'roles'" name="Roles" rules="required|min:1" v-slot="validationContext">
          <b-form-group label="Assigned Roles" label-class="font-weight-bold">
            <b-form-checkbox-group v-model="form.selectedRoles" :options="availableRoles" stacked :state="getValidationState(validationContext)"></b-form-checkbox-group>
            <b-form-invalid-feedback :state="getValidationState(validationContext)">User must have <strong>at least one (1)</strong> role assigned.</b-form-invalid-feedback>
          </b-form-group>
        </ValidationProvider>

        <div class="btn-container mt-4">
          <b-button type="submit" variant="success" class="float-right" :disabled="invalid || editUserLoading">
            <div v-if="editUserLoading">
              <b-spinner small></b-spinner>
              <span class="ml-2">Saving...</span>
            </div>
            <span v-else>Save</span>
          </b-button>
          <b-button variant="danger" class="float-right mr-2" @click="closeModal()">Cancel</b-button>
        </div>
      </b-form>
    </ValidationObserver>
  </b-modal>
</template>

<script>
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import { required, min } from 'vee-validate/dist/rules';

// Validators
extend('min', min);
extend('required', required);
extend('password', {
  params: ['target'],
  validate(value, { target }) {
    return value === target;
  },
  message: 'Password confirmation does not match'
});

export default {
  components: {
    ValidationProvider,
    ValidationObserver
  },

  props: {
    modalId: { type: String, required: true },
    user: { type: Object, required: true },
    roles: { type: Array, required: false },
    fieldToEdit: { type: String, required: true },
    isCurrentUser: { type: Boolean, required: false, default: false }
  },

  data() {
    return {
      form: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        selectedRoles: []
      },
      editUserLoading: false,
      errors: []
    };
  },

  computed: {
    availableRoles() {
      return this.roles.map((role) => {
        return { text: role.description, value: role.name };
      });
    },

    modalTitle() {
      if (this.fieldToEdit === 'roles') return 'Edit Roles';
      if (this.$store.state.user.username === this.user.username) return 'Change Your Password';
      return 'Change Password';
    }
  },

  methods: {
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null;
    },

    resetForm() {
      let roles = [];
      if (this.fieldToEdit === 'roles') roles = this.user.roles;
      this.form = { newPassword: '', selectedRoles: roles };
    },

    closeModal() {
      this.errors = []; // Reset errors
      this.$bvModal.hide(this.modalId);
    },

    async editUser() {
      this.editUserLoading = true;
      this.errors = []; // Reset errors

      const data = {
        userId: this.user.id
      };
      if (this.fieldToEdit === 'password') {
        data.currentPassword = this.form.currentPassword;
        data.password = this.form.newPassword;
      } else if (this.fieldToEdit === 'roles') {
        data.roles = this.form.selectedRoles;
      }
      try {
        await this.$http.put(`/users/${data.userId}/update-${this.fieldToEdit}`, data);
        this.editUserLoading = false;
        this.closeModal();
        this.$emit('editedUser', { user: { username: this.user.username, email: this.user.email }, editFunction: this.fieldToEdit });
      } catch (err) {
        if (!err.response) {
          this.errors.push('Error: A network error occurred.');
        } else {
          if (Array.isArray(err.response.data.message)) {
            this.errors = err.response.data.message;
          } else {
            this.errors.push(err.response.data.message || 'Something went wrong while trying to edit user.');
          }
        }
        this.editUserLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
